import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Article as ArticleIcon,
  CheckCircle as PublishIcon,
  Unpublished as UnpublishIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSBlogPost, CMSBlogCategory } from '../../../services';

const BlogPosts: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<CMSBlogPost[]>([]);
  const [categories, setCategories] = useState<CMSBlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; post: CMSBlogPost | null }>({
    open: false,
    post: null,
  });

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.categoria = categoryFilter;

      const response = await cmsService.getBlogPosts(params);
      setPosts(response.data.data || []);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching posts:', err);
      setError(err.message || t('cms.errorLoadingPosts'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, categoryFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await cmsService.getBlogCategories();
      setCategories(response.data.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePublish = async (post: CMSBlogPost) => {
    try {
      if (post.status === 'publicado') {
        await cmsService.unpublishBlogPost(post.id);
      } else {
        await cmsService.publishBlogPost(post.id);
      }
      fetchPosts();
    } catch (err: any) {
      setError(err.message || t('cms.errorChangingPostStatus'));
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.post) return;
    try {
      await cmsService.deleteBlogPost(deleteDialog.post.id);
      setDeleteDialog({ open: false, post: null });
      fetchPosts();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingPost'));
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'publicado':
        return <Chip size="small" color="success" label={t('cms.published')} />;
      case 'rascunho':
        return <Chip size="small" color="default" label={t('cms.draft')} />;
      case 'arquivado':
        return <Chip size="small" color="warning" label={t('cms.archived')} />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  const filteredPosts = posts.filter(post => {
    if (!searchTerm) return true;
    return (
      post.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.resumo?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const stats = {
    total: totalCount,
    published: posts.filter(p => p.status === 'publicado').length,
    drafts: posts.filter(p => p.status === 'rascunho').length,
  };

  return (
    <Box p={3}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.blogPostsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.blogPostsSubtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/cms/blog/posts/new')}
        >
          {t('cms.newArticle')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('cms.totalArticles')}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('cms.publishedArticles')}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.published}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('cms.drafts')}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="text.secondary">
                {stats.drafts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('cms.searchPosts')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.status')}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t('cms.allFilter')}</MenuItem>
                <MenuItem value="publicado">{t('cms.publishedFilter')}</MenuItem>
                <MenuItem value="rascunho">{t('cms.draftsFilter')}</MenuItem>
                <MenuItem value="arquivado">{t('cms.archivedFilter')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.category')}
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t('common.allFemale')}</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cms.titleColumn')}</TableCell>
                <TableCell>{t('common.category')}</TableCell>
                <TableCell>{t('cms.authorColumn')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell align="center">{t('cms.views')}</TableCell>
                <TableCell>{t('common.date')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredPosts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <ArticleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {t('cms.noPostFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPosts.map((post) => (
                  <TableRow key={post.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {post.titulo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          /{post.slug}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {post.categoria_nome ? (
                        <Chip size="small" label={post.categoria_nome} variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          {t('cms.noCategory')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {post.autor_nome || t('cms.noAuthor')}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(post.status)}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {post.visualizacoes || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {post.data_publicacao
                          ? new Date(post.data_publicacao).toLocaleDateString('pt-PT', { timeZone: 'UTC' })
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={post.status === 'publicado' ? t('cms.unpublish') : t('cms.publish')}>
                        <IconButton
                          size="small"
                          onClick={() => handlePublish(post)}
                          color={post.status === 'publicado' ? 'warning' : 'success'}
                        >
                          {post.status === 'publicado' ? <UnpublishIcon /> : <PublishIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/cms/blog/posts/${post.id}`)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, post })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('cms.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            t('cms.paginationDisplay', { from, to, count: count !== -1 ? count : `>=${to}` })
          }
        />
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, post: null })}>
        <DialogTitle>{t('cms.deletePost')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeletePost', { name: deleteDialog.post?.titulo })}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t('cms.actionIrreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, post: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlogPosts;
